export interface Amenities {
  parking: boolean;
  shower: boolean;
  hotWater: boolean;
  lighting: boolean;
  cafeteria: boolean;
  lockerRoom: boolean;
  tribune: boolean;
  toilet: boolean;
  prayerRoom: boolean;
  firstAid: boolean;
  wifi: boolean;
  security: boolean;
  cameraSecurity: boolean;
  shoeRental: boolean;
  ballProvided: boolean;
  vestProvided: boolean;
}

export type FieldType = 'indoor' | 'outdoor' | 'hybrid';

export interface TimeRangePrice {
  startHour: number; // e.g. 18
  endHour: number;   // e.g. 24
  price: number;     // e.g. 500
  label?: string;    // e.g. "Akşam Tarifesi"
}

export interface Field {
  id: string;
  name: string;
  district: string;
  lat: number;
  lng: number;
  mapsUrl?: string;
  locationInputType?: 'maps-link' | 'coordinates';
  address: string;
  pricePerHour: number;
  rating: number;
  reviewCount: number;
  size: '5v5' | '6v6' | '7v7' | '8v8' | '9v9' | '10v10' | '11v11';
  sizeOptions?: Array<'5v5' | '6v6' | '7v7' | '8v8' | '9v9' | '10v10' | '11v11'>;
  images: string[];
  amenities: Amenities;
  turfType: string;
  phone: string;
  openingHour: number;
  closingHour: number;
  availableTimeRanges?: string; // ex: "09:30-12:00,13:15-23:45"
  availableTimeRangesByDay?: Partial<Record<DayKey, string>>;
  slotIntervalMinutes?: 60;
  subscriptionPlan?: 'free' | 'fan' | 'pro';
  subscriptionEnabled?: boolean;
  subscriptionPrice?: number;
  subscriptionRules?: string;
  ownerId: string;
  description?: string;
  // New properties
  fieldType?: FieldType;
  isReservationPaused?: boolean;
  reservationPausedMessage?: string;
  pricingByTimeRange?: TimeRangePrice[];
  width?: number;     // metres
  length?: number;    // metres
  floorHeating?: boolean;
  roofType?: 'none' | 'open' | 'closed' | 'retractable';
  hasScoreboard?: boolean;
  hasGoalNets?: boolean;
  yearBuilt?: number;
  lastRenovation?: string;
  maxCapacity?: number;
  waterPrice?: number;
  shoeRentalPrice?: number;
  paymentIban?: string;
  paymentBankName?: string;
  paymentAccountName?: string;
  paymentNote?: string;
}

export interface OwnerProfile {
  id: string;
  fullName: string;
  username: string;
  email?: string;
  phone: string;
  password: string;
  district: string;
  status: 'pending' | 'approved' | 'rejected';
  suspended?: boolean;
  createdAt: string;
}

export interface CustomerProfile {
  id: string;
  fullName: string;
  username: string;
  email?: string;
  phone: string;
  password?: string;
  googleId?: string;
  suspended?: boolean;
  createdAt: string;
}

export type DayKey = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';

export type UserSession =
  | { role: 'admin' }
  | { role: 'owner'; ownerId: string }
  | { role: 'customer'; userId: string };

export interface TimeSlot {
  fieldId: string;
  date: string; // YYYY-MM-DD
  hour: number;
  label?: string;
  isBooked: boolean;
  bookedBy?: string;
}

export interface Booking {
  id: string;
  fieldId: string;
  fieldName: string;
  date: string;
  hour: number;
  duration: number;
  teamName: string;
  contactName: string;
  contactPhone: string;
  totalPrice: number;
  customerId?: string;
  status:
    | 'pending'
    | 'pending_owner_approval'
    | 'awaiting_payment'
    | 'payment_review'
    | 'confirmed'
    | 'cancelled';
  paymentProofUrl?: string;
  paymentProofNote?: string;
  paymentProofFileName?: string;
  paymentProofSubmittedAt?: string;
  paymentProofRejectedReason?: string;
  paymentReviewedAt?: string;
  createdAt: string;
}

export type ReminderType = 'reminder_24h' | 'reminder_2h';

export interface ReminderPlan {
  id: string;
  bookingId: string;
  userId?: string;
  contactPhone?: string;
  fieldId: string;
  fieldName: string;
  date: string;
  hour: number;
  triggerAt: string;
  type: ReminderType;
  sentAt?: string;
}

export type NotificationType =
  | ReminderType
  | 'waitlist_slot_opened'
  | 'waitlist_joined'
  | 'booking_confirmed_customer'
  | 'booking_confirmed_owner'
  | 'booking_request_owner'
  | 'booking_approved_customer'
  | 'booking_paid_customer'
  | 'booking_paid_owner'
  | 'booking_payment_proof_owner'
  | 'booking_payment_rejected_customer'
  | 'booking_cancelled_customer'
  | 'booking_cancelled_owner'
  | 'booking_rescheduled_customer'
  | 'booking_rescheduled_owner'
  | 'subscription_request_owner'
  | 'subscription_approved_customer'
  | 'subscription_activated_customer'
  | 'subscription_activated_owner'
  | 'subscription_cancelled_customer'
  | 'subscription_cancelled_owner';

export type NotificationPreferenceKey =
  | 'booking'
  | 'waitlist'
  | 'reminder'
  | 'subscription';

export interface AppNotification {
  id: string;
  userId?: string;
  contactPhone?: string;
  type: NotificationType;
  title: string;
  message: string;
  fieldId?: string;
  bookingId?: string;
  date?: string;
  hour?: number;
  createdAt: string;
  read: boolean;
}

export interface WaitlistEntry {
  id: string;
  fieldId: string;
  fieldName: string;
  date: string;
  hour: number;
  label: string;
  customerId?: string;
  contactName: string;
  contactPhone: string;
  createdAt: string;
  status: 'active' | 'notified' | 'converted' | 'cancelled';
  notifiedAt?: string;
  convertedAt?: string;
  bookingId?: string;
}

export interface FieldReview {
  id: string;
  fieldId: string;
  customerId: string;
  customerName: string;
  rating: number; // 1..5
  comment: string;
  createdAt: string;
}

export interface OwnerStats {
  totalRevenue: number;
  monthlyRevenue: number;
  totalBookings: number;
  monthlyBookings: number;
  occupancyRate: number;
  averageRating: number;
}

export interface SearchFilters {
  district: string;
  date: string;
  time: string;
  size: string;
  minPrice: number;
  maxPrice: number;
}

export interface AuditLogEntry {
  id: string;
  actorRole: 'admin' | 'owner' | 'customer' | 'system';
  actorId?: string;
  action: string;
  targetType: 'booking' | 'field' | 'owner' | 'customer' | 'subscription' | 'system';
  targetId?: string;
  meta?: Record<string, unknown>;
  createdAt: string;
}

export const FIELD_TYPE_LABELS: Record<FieldType, { label: string; icon: string; description: string }> = {
  indoor: { label: 'Kapalı Saha', icon: '🏠', description: 'Çatılı, kapalı alan' },
  outdoor: { label: 'Açık Saha', icon: '☀️', description: 'Açık hava sahası' },
  hybrid: { label: 'Hibrit', icon: '🔄', description: 'Açılır-kapanır çatı' },
};

export const ROOF_TYPE_LABELS: Record<NonNullable<Field['roofType']>, string> = {
  none: 'Çatı Yok',
  open: 'Açık Çatı / Tente',
  closed: 'Kapalı Çatı',
  retractable: 'Açılır-Kapanır Çatı',
};

export const AMENITY_LABELS: Record<keyof Amenities, { label: string; icon: string }> = {
  parking: { label: 'Otopark', icon: '🅿️' },
  shower: { label: 'Duş', icon: '🚿' },
  hotWater: { label: 'Sıcak Su', icon: '♨️' },
  lighting: { label: 'Aydınlatma', icon: '💡' },
  cafeteria: { label: 'Kafeterya', icon: '☕' },
  lockerRoom: { label: 'Soyunma Odası', icon: '🚪' },
  tribune: { label: 'Tribün', icon: '🏟️' },
  toilet: { label: 'Tuvalet', icon: '🚻' },
  prayerRoom: { label: 'Mescit / İbadet Yeri', icon: '🕌' },
  firstAid: { label: 'İlk Yardım', icon: '🏥' },
  wifi: { label: 'Wi-Fi', icon: '📶' },
  security: { label: 'Güvenlik', icon: '👮' },
  cameraSecurity: { label: 'Kamera Sistemi', icon: '📹' },
  shoeRental: { label: 'Krampon Kiralama', icon: '👟' },
  ballProvided: { label: 'Top Mevcut', icon: '⚽' },
  vestProvided: { label: 'Yelek Mevcut', icon: '🦺' },
};
